import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Link, useLocation } from "react-router-dom";

import useSmqPermissions from "../hooks/useSmqPermissions";
import useSMQSidebarStyles from "./SMQSidebartreeView.styles";

const DEFAULT_LINKS = {
  dashboard: "/smq_nc/dashboard",
  nouvelle: "/smq_nc/add",
  registre: "/smq_nc/registre",
  reouvertes: "/smq_nc/reouvertes",
  analyser: "/smq_nc/analyser",
  efficacite: "/smq_nc/suivi-efficacite",
  import: "/smq_nc/import",
  export: "/smq_nc/export",
  // sauvegarde: "/smq_nc/sauvegarde",
  corbeille: "/smq_nc/corbeille",
  declencheurs: "/smq_nc/parametrage/types-declencheur",
  exigences: "/smq_nc/parametrage/types-exigence",
  actions: "/smq_nc/parametrage/types-action",
  referentiel: "/smq_nc/parametrage-processus/referentiel",
  cartographie: "/smq_nc/parametrage-processus/cartographie",
  directions: "/smq_nc/parametrage-processus/directions",
  processus: "/smq_nc/parametrage-processus/processus",
  guide: "/smq_nc/guide",
};

const GUIDE_ITEMS = [
  { key: "guide", icon: "📖", label: "Guide utilisateur" },
];

const NAVIGATION_ITEMS = [
  { key: "dashboard", icon: "📊", label: "Tableau de bord" },
  { key: "nouvelle", icon: "➕", label: "Nouvelle NC", badgeKey: "step" },
  { key: "registre", icon: "📋", label: "Registre des NC", badgeKey: "total" },
  { key: "analyser", icon: "🔬", label: "Analyser NC" },
  { key: "reouvertes", icon: "↪", label: "NC réouvertes" },
  { key: "efficacite", icon: "📈", label: "Suivi efficacité" },
];

const ACCORDION_SECTIONS = [
  {
    key: "donnees",
    label: "Données",
    items: [
      { key: "import", icon: "📥", label: "Importer des données" },
      { key: "export", icon: "📤", label: "Exporter Excel" },
      // { key: "sauvegarde", icon: "💾", label: "Sauvegarde", badgeKey: "late" },
      { key: "corbeille", icon: "🗑️", label: "Corbeille", badgeKey: "trash" },
    ],
  },
  {
    key: "parametrage",
    label: "Paramétrage",
    items: [
      { key: "declencheurs", icon: "⚙️", label: "Types de déclencheur" },
      { key: "exigences", icon: "📋", label: "Types d'exigence" },
      { key: "actions", icon: "🎯", label: "Types d'action" },
    ],
  },
  {
    key: "processus",
    label: "Paramétrage des processus",
    items: [
      { key: "referentiel", icon: "🗂️", label: "Référentiel processus" },
      { key: "cartographie", icon: "🗺️", label: "Cartographie" },
      { key: "directions", icon: "🏢", label: "Configuration des directions" },
      { key: "processus", icon: "⚙️", label: "Processus" },
    ],
  },
];

const normalizePath = (path) => {
  const cleanPath = String(path || "").split("?")[0].split("#")[0].trim();
  if (!cleanPath) return "/";
  return cleanPath.length > 1 ? cleanPath.replace(/\/+$/, "") : cleanPath;
};

const hasVisibleBadge = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (typeof value === "number") return value > 0;
  return String(value).trim() !== "0";
};

function SMQMenuItems({
  items,
  activeItemKey,
  badgeCounts,
  classes,
  links,
  onNavigate,
  onSelect,
}) {
  const handleItemClick = (item) => {
    if (typeof onSelect === "function") {
      onSelect(item.key, { ...item, link: links[item.key] });
    }
    if (typeof onNavigate === "function") {
      onNavigate();
    }
  };

  return (
    <ul className={classes.menuList}>
      {items.map((item) => {
        const isActive = activeItemKey === item.key;
        const badgeValue = item.badgeKey ? badgeCounts[item.badgeKey] : null;

        return (
          <li className={classes.menuItem} key={item.key}>
            <Link
              aria-current={isActive ? "page" : undefined}
              className={clsx(classes.menuLink, {
                [classes.menuLinkActive]: isActive,
              })}
              onClick={() => handleItemClick(item)}
              to={links[item.key]}
            >
              <span
                aria-hidden="true"
                className={clsx(classes.icon, {
                  [classes.iconActive]: isActive,
                })}
              >
                {item.icon}
              </span>
              <span className={classes.itemLabel}>{item.label}</span>
              {hasVisibleBadge(badgeValue) && (
                <span className={classes.badge}>{badgeValue}</span>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function SMQSidebartreeView({
  activeKey,
  badgeCounts = {},
  links = {},
  onNavigate,
  onSelect,
}) {
  const classes = useSMQSidebarStyles();
  const { canCreate, canRead, canTreat, isAdmin } = useSmqPermissions();
  const location = useLocation();
  const resolvedLinks = useMemo(() => ({ ...DEFAULT_LINKS, ...links }), [links]);
  const visibleNavigationItems = useMemo(
    () =>
      NAVIGATION_ITEMS.filter((item) => {
        if (item.key === "nouvelle") return canCreate;
        if (item.key === "analyser") return canTreat;
        return true;
      }),
    [canCreate, canTreat]
  );

  const visibleAccordionSections = useMemo(
    () =>
      ACCORDION_SECTIONS.reduce((sections, section) => {
        if (section.key === "parametrage" && !isAdmin) return sections;

        const items = section.items.filter(
          (item) =>
            !["corbeille", "import"].includes(item.key) || isAdmin
        );

        return items.length ? [...sections, { ...section, items }] : sections;
      }, []),
    [isAdmin]
  );

  const allItems = useMemo(
    () => [
      ...visibleNavigationItems,
      ...visibleAccordionSections.flatMap((section) => section.items),
      ...GUIDE_ITEMS,
    ],
    [visibleAccordionSections, visibleNavigationItems]
  );

  const activeKeyFromPath = useMemo(() => {
    const pathname = normalizePath(location.pathname);
    const matchedItem = allItems
      .filter((item) => {
        const itemPath = normalizePath(resolvedLinks[item.key]);
        return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
      })
      .sort(
        (first, second) =>
          normalizePath(resolvedLinks[second.key]).length -
          normalizePath(resolvedLinks[first.key]).length
      )[0];

    return matchedItem?.key || null;
  }, [allItems, location.pathname, resolvedLinks]);

  const activeItemKey = activeKeyFromPath || activeKey || null;
  const activeSectionKey = useMemo(
    () =>
      visibleAccordionSections.find((section) =>
        section.items.some((item) => item.key === activeItemKey)
      )?.key || null,
    [activeItemKey, visibleAccordionSections]
  );

  const [expandedSections, setExpandedSections] = useState(() =>
    activeSectionKey ? [activeSectionKey] : []
  );

  useEffect(() => {
    if (!activeSectionKey) return;
    setExpandedSections((currentSections) =>
      currentSections.includes(activeSectionKey)
        ? currentSections
        : [...currentSections, activeSectionKey]
    );
  }, [activeSectionKey]);

  const toggleSection = (sectionKey) => {
    setExpandedSections((currentSections) =>
      currentSections.includes(sectionKey)
        ? currentSections.filter((key) => key !== sectionKey)
        : [...currentSections, sectionKey]
    );
  };

  if (!canRead) return null;

  return (
    <nav className={classes.sidebarRoot} aria-label="Navigation SMQ">
      <div
        aria-labelledby="smq-navigation-title"
        className={classes.section}
        role="group"
      >
        <div className={classes.sectionTitle} id="smq-navigation-title">
          Navigation
        </div>
        <SMQMenuItems
          activeItemKey={activeItemKey}
          badgeCounts={badgeCounts}
          classes={classes}
          items={visibleNavigationItems}
          links={resolvedLinks}
          onNavigate={onNavigate}
          onSelect={onSelect}
        />
      </div>

      {visibleAccordionSections.map((section) => {
        const isExpanded = expandedSections.includes(section.key);
        const panelId = `smq-${section.key}-panel`;
        const triggerId = `smq-${section.key}-trigger`;

        return (
          <div className={classes.section} key={section.key}>
            <button
              aria-controls={panelId}
              aria-expanded={isExpanded}
              className={clsx(classes.accordionButton, {
                [classes.accordionButtonOpen]: isExpanded,
              })}
              id={triggerId}
              onClick={() => toggleSection(section.key)}
              type="button"
            >
              <span className={classes.accordionLabel}>{section.label}</span>
              <span
                aria-hidden="true"
                className={clsx(classes.chevron, {
                  [classes.chevronOpen]: isExpanded,
                })}
              >
                ▾
              </span>
            </button>
            <div
              aria-labelledby={triggerId}
              className={classes.accordionPanel}
              hidden={!isExpanded}
              id={panelId}
              role="region"
            >
              <div className={classes.nestedList}>
                <SMQMenuItems
                  activeItemKey={activeItemKey}
                  badgeCounts={badgeCounts}
                  classes={classes}
                  items={section.items}
                  links={resolvedLinks}
                  onNavigate={onNavigate}
                  onSelect={onSelect}
                />
              </div>
            </div>
          </div>
        );
      })}

      <div
        aria-labelledby="smq-guide-title"
        className={classes.section}
        role="group"
      >
        <div className={classes.sectionTitle} id="smq-guide-title">
          Aide
        </div>
        <SMQMenuItems
          activeItemKey={activeItemKey}
          badgeCounts={badgeCounts}
          classes={classes}
          items={GUIDE_ITEMS}
          links={resolvedLinks}
          onNavigate={onNavigate}
          onSelect={onSelect}
        />
      </div>
    </nav>
  );
}

export default SMQSidebartreeView;
